<template>
  <table
    :class="['table', 'table-sm', 'table-bordered', tableClass]"
    cellspacing="0"
    cellpadding="0"
  >
    <colgroup>
      <col
        v-for="field in fields"
        :key="'col-' + field.key"
        :style="field.thStyle"
      />
    </colgroup>
    <caption
      v-if="leadingTitle"
      class="invoice-li-group-title"
    >
      {{ leadingTitle }}
    </caption>
    <thead class="thead-light">
      <tr>
        <th
          v-for="field in fields"
          :key="'th-' + field.key"
          :class="field.thClass || field.class"
          :style="field.thStyle"
        >
          {{ field.label }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-if="!items.length">
        <td :colspan="colCount" class="text-center text-muted">{{ emptyText }}</td>
      </tr>
      <template v-else>
        <template v-for="row in displayRows">
          <tr
            v-if="row._optionalTitleRow"
            :key="row._rowKey"
            class="invoice-li-group-title-row"
          >
            <td :colspan="colCount" class="invoice-li-group-title">
              {{ row.title }}
            </td>
          </tr>
          <tr
            v-else-if="row._repeatHeaders"
            :key="row._rowKey"
            class="invoice-li-repeat-header-row thead-light"
          >
            <th
              v-for="field in fields"
              :key="row._rowKey + '-h-' + field.key"
              :class="field.thClass || field.class"
              :style="field.thStyle"
            >
              {{ field.label }}
            </th>
          </tr>
          <tr v-else :key="row._rowKey">
            <td
              v-for="field in fields"
              :key="row._rowKey + '-' + field.key"
              :class="cellClass(field, row.item)"
            >
              {{ cellValue(field, row.item) }}
            </td>
          </tr>
        </template>
      </template>
    </tbody>
    <tfoot v-if="hasFooterRow || $slots['after-total']">
      <tr v-if="hasFooterRow" class="invoice-li-total-row">
        <td
          v-for="field in fields"
          :key="'tf-' + field.key"
          :class="footerCellClass(field)"
        >
          {{ footerCellValue(field) }}
        </td>
      </tr>
      <slot name="after-total" />
    </tfoot>
  </table>
</template>

<script>
import { resolveOptionalOverlayTitle } from '@/utils/invoice-calculation-engine'

export default {
  name: 'InvoicePreviewItemsTable',
  props: {
    items: { type: Array, default: () => [] },
    fields: { type: Array, default: () => [] },
    tableClass: { type: String, default: 'mb-0 invoice-line-items-table' },
    emptyText: { type: String, default: 'No line items' },
    showOptionalTitles: { type: Boolean, default: false },
    // Optional last row on the same column grid as thead/tbody
    // ({ label: 'Total', values: { igstAmount: '…', lineTotal: '…' } }).
    footerRow: { type: Object, default: null },
  },
  computed: {
    colCount() {
      return (this.fields && this.fields.length) || 1
    },
    hasFooterRow() {
      return !!(this.footerRow && (this.footerRow.label || (this.footerRow.values && Object.keys(this.footerRow.values).length)))
    },
    footerLabelKey() {
      const fields = this.fields || []
      const desc = fields.find((f) => {
        if (!f) return false
        const k = String(f.key || '').toLowerCase().replace(/[^a-z0-9]/g, '')
        const cls = `${f.class || ''} ${f.thClass || ''}`
        return (
          k === 'itemname'
          || k === 'title'
          || k === 'description'
          || k === 'descriptionofgoods'
          || k === 'particulars'
          || cls.includes('invoice-col-desc')
        )
      })
      return (desc && desc.key) || (fields[0] && fields[0].key) || ''
    },
    titleGroups() {
      const items = this.items || []
      if (!items.length || !this.showOptionalTitles) {
        return [{ title: '', items: items.map((item, idx) => ({ _rowKey: `r-${idx}`, item })) }]
      }
      const groups = []
      let current = null
      items.forEach((item, idx) => {
        const title = resolveOptionalOverlayTitle(item)
        if (!current || current.title !== title) {
          current = { title, items: [] }
          groups.push(current)
        }
        current.items.push({ _rowKey: `r-${idx}`, item })
      })
      return groups
    },
    leadingTitle() {
      if (!this.showOptionalTitles || !this.items.length) return ''
      const first = this.titleGroups[0]
      return first && first.title ? first.title : ''
    },
    displayRows() {
      const groups = this.titleGroups
      if (!this.showOptionalTitles) {
        return (this.items || []).map((item, idx) => ({
          _optionalTitleRow: false,
          _repeatHeaders: false,
          _rowKey: `r-${idx}`,
          item,
        }))
      }
      const out = []
      groups.forEach((group, gIdx) => {
        if (gIdx > 0 && group.title) {
          out.push({
            _optionalTitleRow: true,
            _repeatHeaders: false,
            _rowKey: `t-${gIdx}`,
            title: group.title,
          })
          out.push({
            _optionalTitleRow: false,
            _repeatHeaders: true,
            _rowKey: `h-${gIdx}`,
          })
        }
        group.items.forEach((row) => {
          out.push({
            _optionalTitleRow: false,
            _repeatHeaders: false,
            _rowKey: row._rowKey,
            item: row.item,
          })
        })
      })
      return out
    },
  },
  methods: {
    cellValue(field, item) {
      if (!field || !item) return '—'
      const raw = item[field.key]
      if (typeof field.formatter === 'function') {
        return field.formatter(raw, field.key, item)
      }
      return raw == null || raw === '' ? '—' : raw
    },
    cellClass(field, item) {
      if (!field) return ''
      const raw = item ? item[field.key] : ''
      if (typeof field.tdClass === 'function') return field.tdClass(raw, field.key, item) || ''
      return field.tdClass || field.class || ''
    },
    footerCellValue(field) {
      if (!field || !this.footerRow) return ''
      const values = this.footerRow.values || {}
      if (Object.prototype.hasOwnProperty.call(values, field.key) && values[field.key] != null && values[field.key] !== '') {
        return values[field.key]
      }
      if (field.key === this.footerLabelKey) return this.footerRow.label || 'Total'
      return ''
    },
    footerCellClass(field) {
      const extra = ['invoice-li-total-td']
      if (field && field.key === this.footerLabelKey) extra.push('invoice-li-total-label')
      const base = this.cellClass(field, { [field && field.key]: this.footerCellValue(field) })
      return [base, ...extra].filter(Boolean).join(' ')
    },
  },
}
</script>

<style scoped>
caption.invoice-li-group-title {
  caption-side: top !important;
  text-align: left;
  font-weight: 700;
  background: transparent;
  letter-spacing: 0.01em;
  padding: 0.2rem 0.15rem 0.4rem;
  color: inherit;
}
.invoice-li-group-title-row > .invoice-li-group-title,
td.invoice-li-group-title {
  font-weight: 700;
  text-align: left;
  background: transparent !important;
  letter-spacing: 0.01em;
  padding: 0.45rem 0.15rem 0.35rem;
}
.invoice-li-repeat-header-row > th {
  font-weight: 700;
}
.invoice-li-total-td {
  overflow: visible;
  box-sizing: border-box;
}
</style>
